import { getReportFooterText } from '../reportFooter';
import type { PersonLabels } from '@/hooks/usePersonLabels';
export interface OverdueAssetsHeaderInfo {
  fromDate?: string | null;
  toDate?: string | null;
  individualName?: string | null;
  individualIe?: string | null;
  linFilter?: string | null;
  ieFilter?: string | null;
  qtyAuthLowerThanIssued?: boolean;
  totalCost?: number | null;
  totalCount?: number | null;
}

export interface OverdueAssetsItem {
  individualName?: string | null;
  individualIe?: string | null;
  lin?: string | null;
  nsn?: string | null;
  nomenclature: string;
  cost?: number | null;
  totalCost?: number | null;
  recoverableFlag?: string | null;
  quantityIssued?: number | null;
  quantityAuthorized?: number | null;
  thrPhr?: string | null;
  dueDate?: string | null;
}

export interface OverdueAssetsTemplateOptions {
  generatedAt: Date;
  header: OverdueAssetsHeaderInfo;
  items: OverdueAssetsItem[];
  personLabels?: PersonLabels;
  ieKitLabel?: string;
}

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

/**
 * Sanitizes text for safe HTML rendering by escaping HTML entities.
 * Protects against XSS attacks by converting special characters to their HTML entity equivalents.
 * @param value - The value to sanitize
 * @returns The sanitized string or '—' if value is empty/null
 */
const sanitizeText = (value: string | null | undefined): string => {
  const trimmed = value?.toString().trim();
  if (!trimmed) {
    return '—';
  }
  return escapeHtml(trimmed);
};

const formatCurrency = (value: number | null | undefined): string => {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return '—';
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2,
  }).format(value);
};

const formatDate = (value: string | null | undefined): string => {
  if (!value) {
    return '—';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return sanitizeText(value);
  }
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
};

const formatDateTime = (value: Date): string =>
  new Intl.DateTimeFormat('en-US', {
    dateStyle: 'long',
    timeStyle: 'short',
  }).format(value);

const buildItemsTable = (items: OverdueAssetsItem[]): string => {
  if (items.length === 0) {
    return `
      <tr>
        <td colspan="13" class="empty-row">
          No overdue assets matched the selected filters.
        </td>
      </tr>
    `;
  }

  return items
    .map(
      (item, index) => `
        <tr>
          <td class="index">${index + 1}</td>
          <td>${sanitizeText(item.individualName)}</td>
          <td>${sanitizeText(item.individualIe)}</td>
          <td>${sanitizeText(item.lin)}</td>
          <td>${sanitizeText(item.nsn)}</td>
          <td>${sanitizeText(item.nomenclature)}</td>
          <td class="numeric">${formatCurrency(item.cost ?? null)}</td>
          <td class="numeric">${formatCurrency(item.totalCost ?? null)}</td>
          <td>${sanitizeText(item.recoverableFlag)}</td>
          <td class="qty">${sanitizeText(
            item.quantityIssued !== null && item.quantityIssued !== undefined
              ? String(item.quantityIssued)
              : '',
          )}</td>
          <td class="qty">${sanitizeText(
            item.quantityAuthorized !== null && item.quantityAuthorized !== undefined
              ? String(item.quantityAuthorized)
              : '',
          )}</td>
          <td>${sanitizeText(item.thrPhr)}</td>
          <td>${formatDate(item.dueDate ?? null)}</td>
        </tr>
      `,
    )
    .join('');
};

export const buildOverdueAssetsReportHtml = ({
  generatedAt,
  header,
  items,
  personLabels,
  ieKitLabel = 'IE Kit',
}: OverdueAssetsTemplateOptions): string => {
  const pl = personLabels ?? { label: 'Individual', labelPlural: 'Individuals', lower: 'individual', lowerPlural: 'individuals', article: 'an' as const };
  const generatedLabel = formatDateTime(generatedAt);
  const fromDate = formatDate(header.fromDate ?? null);
  const toDate = formatDate(header.toDate ?? null);
  const individualName = header.individualName ? sanitizeText(header.individualName) : '—';
  const individualIe = header.individualIe ? sanitizeText(header.individualIe) : '—';
  const linFilter = header.linFilter ? sanitizeText(header.linFilter) : '—';
  const ieFilter = header.ieFilter ? sanitizeText(header.ieFilter) : '—';
  const qtyAuthLowerLabel = header.qtyAuthLowerThanIssued ? 'Yes' : 'No';
  const totalCostLabel = formatCurrency(header.totalCost ?? null);
  const totalCountLabel =
    typeof header.totalCount === 'number' && Number.isFinite(header.totalCount)
      ? String(header.totalCount)
      : '0';

  const itemsTable = buildItemsTable(items);

  return `<!DOCTYPE html>
<html lang="en">
  <head>
    <meta charset="utf-8" />
    <title>Overdue Assets</title>
    <style>
      :root {
        color-scheme: light;
      }

      * {
        box-sizing: border-box;
      }

      body {
        margin: 16px;
        font-family: 'Roboto', 'Helvetica Neue', Arial, sans-serif;
        color: #15263c;
        background: #ffffff;
      }

      header {
        margin-bottom: 24px;
      }

      .brand {
        font-size: 14px;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        color: #3a82f6;
        font-weight: 600;
      }

      .title {
        font-size: 28px;
        font-weight: 700;
        margin: 0;
      }

      .generated {
        margin-top: 4px;
        font-size: 14px;
        color: #4a5c72;
      }

      .filters-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
        gap: 8px 16px;
        margin-top: 16px;
        margin-bottom: 16px;
      }

      .filter-label {
        font-size: 12px;
        font-weight: 600;
        letter-spacing: 0.06em;
        text-transform: uppercase;
        color: #5c6f85;
        margin-bottom: 2px;
      }

      .filter-value {
        font-size: 14px;
        color: #15263c;
      }

      .summary-row {
        display: flex;
        flex-wrap: wrap;
        gap: 16px;
        margin: 16px 0;
        padding: 8px 12px;
        background: #e9f1ff;
        border-radius: 4px;
      }

      .summary-item {
        font-size: 14px;
        font-weight: 600;
        color: #15263c;
      }

      table {
        width: 100%;
        border-collapse: collapse;
        margin-bottom: 24px;
        border: 1px solid #d7e3f4;
      }

      thead th {
        background: #e9f1ff;
        text-align: left;
        font-size: 12px;
        letter-spacing: 0.05em;
        text-transform: uppercase;
        padding: 10px 12px;
        color: #4a5c72;
        border-bottom: 1px solid #d7e3f4;
      }

      tbody td {
        padding: 12px;
        border-bottom: 1px solid #edf3fc;
        font-size: 14px;
        color: #1f2f45;
      }

      tbody tr:last-child td {
        border-bottom: none;
      }

      tbody tr:nth-child(even) td {
        background: #f9fbff;
      }

      .index {
        width: 32px;
        text-align: center;
        font-weight: 600;
        color: #4a5c72;
      }

      .numeric {
        text-align: right;
        font-variant-numeric: tabular-nums;
      }

      .qty {
        text-align: center;
        font-variant-numeric: tabular-nums;
      }

      .empty-row {
        text-align: center;
        font-style: italic;
        color: #6b7c92;
        padding: 24px 12px;
      }

      footer {
        margin-top: 32px;
        font-size: 12px;
        color: #5c6f85;
        text-align: center;
      }

      @media print {
        body {
          margin: 8mm;
        }
      }
    </style>
  </head>
  <body>
    <header>
      <div class="brand">eQuip Transaction Suite</div>
      <h1 class="title">Overdue Assets</h1>
      <div class="generated">Generated ${escapeHtml(generatedLabel)}</div>

      <div class="filters-grid">
        <div>
          <div class="filter-label">From Date</div>
          <div class="filter-value">${fromDate}</div>
        </div>
        <div>
          <div class="filter-label">To Date</div>
          <div class="filter-value">${toDate}</div>
        </div>
        <div>
          <div class="filter-label">${pl.label} (Optional)</div>
          <div class="filter-value">${individualName}</div>
        </div>
        <div>
          <div class="filter-label">${pl.label} ${ieKitLabel}</div>
          <div class="filter-value">${individualIe}</div>
        </div>
        <div>
          <div class="filter-label">LIN (comma-separated)</div>
          <div class="filter-value">${linFilter}</div>
        </div>
        <div>
          <div class="filter-label">${ieKitLabel} Filter (LIN)</div>
          <div class="filter-value">${ieFilter}</div>
        </div>
        <div>
          <div class="filter-label">Qty Auth Lower than Qty Issued</div>
          <div class="filter-value">${qtyAuthLowerLabel}</div>
        </div>
      </div>

      <div class="summary-row">
        <div class="summary-item">Total Costs: ${totalCostLabel}</div>
        <div class="summary-item">Total Count: ${totalCountLabel}</div>
      </div>
    </header>

    <section>
      <table>
        <thead>
          <tr>
            <th>#</th>
            <th>${pl.label}</th>
            <th>${pl.label} ${ieKitLabel}</th>
            <th>LIN</th>
            <th>NSN</th>
            <th>Nomenclature</th>
            <th>Cost</th>
            <th>Total Cost</th>
            <th>Recoverable / NonRecoverable</th>
            <th>Qty Issued</th>
            <th>Qty Authorized</th>
            <th>THR</th>
            <th>Due Date</th>
          </tr>
        </thead>
        <tbody>
          ${itemsTable}
        </tbody>
      </table>
    </section>

    <footer>
      Generated ${escapeHtml(generatedLabel)} · ${getReportFooterText()}
    </footer>
  </body>
</html>`;
};
